export const reconcilePickedIdsWithVisible = ({
    pickedIds,
    visibleIds,
}: {
    pickedIds: string[];
    visibleIds: string[];
}) => {
    const visibleIdsSet = new Set(visibleIds);
    const removedIds = pickedIds.filter((pickedId) => !visibleIdsSet.has(pickedId));
    const nextPickedIds = removedIds.length ? pickedIds.filter((pickedId) => visibleIdsSet.has(pickedId)) : pickedIds;

    return {
        removedIds,
        nextPickedIds,
    };
};

export const selectPagedIdsWithLimit = ({
    pickedIds,
    pagedIds,
    maxSelectedItems,
}: {
    pickedIds: string[];
    pagedIds: string[];
    maxSelectedItems: number | null;
}) => {
    const pickedIdsSet = new Set(pickedIds);
    const addCandidateIds = pagedIds.filter((id) => !pickedIdsSet.has(id));
    const availableSlots =
        maxSelectedItems === null ? addCandidateIds.length : Math.max(0, maxSelectedItems - pickedIds.length);
    const addIds = addCandidateIds.slice(0, availableSlots);
    const nextPickedIds = addIds.length ? [...pickedIds, ...addIds] : pickedIds;
    const isSelectionLimitReached = maxSelectedItems !== null && addIds.length < addCandidateIds.length;

    return {
        addIds,
        nextPickedIds,
        isSelectionLimitReached,
    };
};

export const removePagedIds = ({ pickedIds, pagedIds }: { pickedIds: string[]; pagedIds: string[] }) => {
    const pagedIdsSet = new Set(pagedIds);
    const removeIds = pickedIds.filter((pickedId) => pagedIdsSet.has(pickedId));
    const nextPickedIds = removeIds.length ? pickedIds.filter((pickedId) => !pagedIdsSet.has(pickedId)) : pickedIds;

    return {
        removeIds,
        nextPickedIds,
    };
};

export const toggleItemSelection = ({
    checked,
    itemId,
    pickedIds,
    maxSelectedItems,
}: {
    checked: boolean;
    itemId: string;
    pickedIds: string[];
    maxSelectedItems: number | null;
}) => {
    const pickedIdsSet = new Set(pickedIds);

    if (checked) {
        if (pickedIdsSet.has(itemId)) {
            return {
                changed: false,
                addIds: [] as string[],
                removeIds: [] as string[],
                nextPickedIds: pickedIds,
                isSelectionLimitReached: false,
            };
        }

        if (maxSelectedItems !== null && pickedIds.length >= maxSelectedItems) {
            return {
                changed: false,
                addIds: [] as string[],
                removeIds: [] as string[],
                nextPickedIds: pickedIds,
                isSelectionLimitReached: true,
            };
        }

        return {
            changed: true,
            addIds: [itemId],
            removeIds: [] as string[],
            nextPickedIds: [...pickedIds, itemId],
            isSelectionLimitReached: false,
        };
    }

    if (!pickedIdsSet.has(itemId)) {
        return {
            changed: false,
            addIds: [] as string[],
            removeIds: [] as string[],
            nextPickedIds: pickedIds,
            isSelectionLimitReached: false,
        };
    }

    return {
        changed: true,
        addIds: [] as string[],
        removeIds: [itemId],
        nextPickedIds: pickedIds.filter((pickedId) => pickedId !== itemId),
        isSelectionLimitReached: false,
    };
};

type ToggleAllSelectionResult =
    | {
          mode: 'clear';
          nextPickedIds: [];
          isSelectionLimitReached: false;
      }
    | {
          mode: 'select';
          nextPickedIds: string[];
          isSelectionLimitReached: boolean;
      }
    | {
          mode: 'noop';
          nextPickedIds: string[];
          isSelectionLimitReached: boolean;
      };

export const toggleAllSelectionWithLimit = ({
    hasPickedItems,
    allItemIds,
    maxSelectedItems,
}: {
    hasPickedItems: boolean;
    allItemIds: string[];
    maxSelectedItems: number | null;
}): ToggleAllSelectionResult => {
    if (hasPickedItems || !allItemIds.length) {
        return {
            mode: 'clear',
            nextPickedIds: [],
            isSelectionLimitReached: false,
        };
    }

    const availableSlots =
        maxSelectedItems === null ? allItemIds.length : Math.max(0, Math.min(allItemIds.length, maxSelectedItems));

    if (availableSlots === 0) {
        return {
            mode: 'noop',
            nextPickedIds: [],
            isSelectionLimitReached: maxSelectedItems !== null,
        };
    }

    const nextPickedIds = allItemIds.slice(0, availableSlots);
    return {
        mode: 'select',
        nextPickedIds,
        isSelectionLimitReached: maxSelectedItems !== null && availableSlots < allItemIds.length,
    };
};
